import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineXS } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PrimaryBuyForAPlayer } from 'components/primaryBuy/PrimaryBuyForAPlayer';
import { BriefGame } from 'components/so5/BriefGame';
import { isGameStarted } from 'lib/so5';

import { BriefSo5AppearanceScore } from '../BriefSo5AppearanceScore';
import MatchViewDialog from '../MatchViewDialog';
import { PlayerGameScoreStats } from '../PlayerGameScoreStats';
import {
  AppearanceDialogQuery,
  AppearanceDialogQueryVariables,
} from './__generated__/index.graphql';

const SO5_APPEARANCE_DIALOG_QUERY = gql`
  query AppearanceDialogQuery($id: ID!) {
    so5 {
      so5Appearance(id: $id) {
        id
        so5Fixture {
          slug
          startDate
          endDate
        }
        eligiblePlayerGameScores {
          id
          anyGame {
            id
            ...BriefGame_anyGameInterface
          }
          ...PlayerGameScoreStats_playerGameScoreInterface
        }
        scoreStatus
        pictureUrl
        bonus
        ...BriefSo5AppearanceScore_so5Appearance
      }
    }
  }
  ${BriefSo5AppearanceScore.fragments.so5Appearance}
  ${PlayerGameScoreStats.fragments.playerGameScoreInterface}
  ${BriefGame.fragments.anyGameInterface}
` as TypedDocumentNode<AppearanceDialogQuery, AppearanceDialogQueryVariables>;

const Root = styled(Vertical)`
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-width: 600px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  so5AppearanceId: string;
  showUpsell?: boolean;
};

export const So5AppearanceDialog = ({
  open,
  onClose,
  showUpsell,
  so5AppearanceId,
}: Props) => {
  const { formatDate } = useIntlContext();
  const [openMatchView, setOpenMatchView] = useState(false);
  const { data, loading } = useQuery(SO5_APPEARANCE_DIALOG_QUERY, {
    variables: { id: idFromObject(so5AppearanceId) },
  });

  const { so5Appearance } = data?.so5 || {};

  const anyGameStarted = so5Appearance?.eligiblePlayerGameScores.some(pgs =>
    isGameStarted(pgs.anyGame.status)
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={
          so5Appearance && so5Appearance.eligiblePlayerGameScores.length > 2
            ? 'md'
            : 'sm'
        }
        title={
          so5Appearance && (
            <Horizontal center>
              <HeadlineXS as="h3">
                <Horizontal as="span">
                  <span>
                    <FormattedMessage {...fantasy.playerPerformance} />
                  </span>
                  <span>-</span>
                  <span>
                    {formatDate(so5Appearance.so5Fixture.startDate, {
                      month: 'short',
                    })}
                  </span>
                  <span>
                    {formatDate(so5Appearance.so5Fixture.startDate, {
                      day: '2-digit',
                    })}
                    -
                    {formatDate(so5Appearance.so5Fixture.endDate, {
                      day: '2-digit',
                    })}
                  </span>
                </Horizontal>
              </HeadlineXS>
            </Horizontal>
          )
        }
      >
        <>
          {loading && <LoadingIndicator />}
          {so5Appearance && (
            <Root gap={5}>
              <BriefSo5AppearanceScore so5Appearance={so5Appearance} />
              {so5Appearance.eligiblePlayerGameScores.length === 1 && (
                <BriefGame
                  game={so5Appearance.eligiblePlayerGameScores[0].anyGame}
                  onClick={() => setOpenMatchView(true)}
                />
              )}
              {showUpsell && (
                <PrimaryBuyForAPlayer
                  playerSlug={so5Appearance.anyPlayer.slug}
                />
              )}
              {((so5Appearance.scoreStatus !== ScoreStatus.DID_NOT_PLAY &&
                anyGameStarted) ||
                so5Appearance.eligiblePlayerGameScores.length > 1) && (
                <PlayerGameScoreStats
                  playerGameScores={so5Appearance.eligiblePlayerGameScores}
                />
              )}
            </Root>
          )}
        </>
      </Dialog>
      {!!openMatchView && so5Appearance && (
        <MatchViewDialog
          open
          onClose={() => setOpenMatchView(false)}
          gameId={so5Appearance.eligiblePlayerGameScores[0].anyGame.id}
        />
      )}
    </>
  );
};
